import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CaptureswingComponent } from './captureswing/captureswing.component';
import { CompareSwingsComponent } from './compare-swings/compare-swings.component';
import { NgIf, NgForOf, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Swing, SwingResult } from '../../models/swing';
import { swingResultLabel } from '../../utils/swing-result';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { ConfigService } from '../../services/configuration.service';
import { AuthenticationService } from '../../services/authentication.service';
import { PERMISSIONS } from '../../models/constants';
import { AutosaveTextareaComponent } from '../autosave-textarea/autosave-textarea.component';
import { ShotData } from '../../models/shotData';
import { CommonModule } from '@angular/common';
import { Session } from '../../models/session';
import { makeWebmSeekable } from '../../utils/webm-seekable';
import {
  blobToBytes,
  buildSwingMarkdown,
  markdownToBytes,
  swingExportZipName,
  videoFileName,
  zipSwingPackage
} from '../../utils/swing-export';

type Shape = 
  | { type: 'circle'; cx: number; cy: number; r: number; selected: boolean }
  | { type: 'line'; x1: number; y1: number; x2: number; y2: number; selected: boolean };


@Component({
  selector: 'app-session',
  standalone: true,
  imports: [NgIf, CaptureswingComponent, CompareSwingsComponent, FormsModule, NgForOf, NgClass, AutosaveTextareaComponent, CommonModule],
  templateUrl: './session.component.html',
  styleUrl: './session.component.css'
})
export class SessionComponent implements OnInit, OnDestroy{

  constructor(private route: ActivatedRoute, private sessionService:SessionService, private configService:ConfigService, private authenticationService:AuthenticationService, private router:Router) {}

  @ViewChild('reviewFaceOnVideo') faceOnVideoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('reviewDownTheLineVideo') downTheLineVideoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild(CompareSwingsComponent) compareSwings?: CompareSwingsComponent;

  sessionId:number = -1;
  showSwingCapture:boolean = false;
  canCaptureSwing:boolean = false;

  // Menu properties
  activeMenu = '';
  reviewTab: 'this' | 'compare' = 'this';
  
  // Accordion properties
  openAccordions: Set<string> = new Set(['swings', 'result']);
  
  // Notes and tags properties
  swings:Array<SwingViewModel> = [];
  selectedSwing:SwingViewModel|null = null;
  notes = '';
  tags: string[] = ['technique', 'practice'];
  newTag = '';
  selectedResult: SwingResult | null = null;
  faceOnVideoUrl:string = '';
  dtlVideoUrl:string = '';
  hasFaceOnClip = false;
  hasDtlClip = false;
  isFaceOnLoading = false;
  isDtlLoading = false;
  shotData:ShotData|null = null;

  // Playback properties
  playbackSpeed:number = 0.5;
  isPlaying:boolean = false;
  /** Which camera is visible in the compact (phone) review layout. */
  activeReviewCamera: 'faceon' | 'dtl' = 'faceon';
  session:Session|null = null;
  currentTime = 0;
  duration = 0;
  isScrubbing = false;
  readonly sliderStep = 1 / 60;
  readonly frameStep = 1 / 30;
  isBufferingVideos = false;
  private desiredTime = 0;
  private lastCommitAt = 0;
  private seekIssuedAt = 0;
  private seekPump: ReturnType<typeof setInterval> | null = null;
  private readonly minCommitIntervalMs = 150;
  private readonly seekTimeoutMs = 1500;
  private objectUrls: string[] = [];
  private videoLoadToken = 0;
  /** True once clips are fully downloaded and remuxed for seeking. */
  private videosFullyLocal = false;
  private readonly onPointerUpScrubEnd = () => this.onScrubEnd();
  canUseCompareView = true;
  savingStart = false;
  isDownloadingPackage = false;
  private startSeekPending = false;
  private readonly compareViewMediaQuery = '(max-width: 768px), (max-height: 500px) and (orientation: landscape)';
  private readonly onViewportChange = () => this.updateCompareAvailability();


  async ngOnInit() {
    this.updateCompareAvailability();
    window.addEventListener('resize', this.onViewportChange);
    this.sessionId = this.route.snapshot.paramMap.get('id') as unknown as number;
    this.session = await this.sessionService.getSession(this.sessionId);
    if(this.session)
      this.setupSession();
    else {
      this.router.navigate(['/permissiondenied'])
    }
  }

  ngOnDestroy() {
    window.removeEventListener('pointerup', this.onPointerUpScrubEnd);
    window.removeEventListener('resize', this.onViewportChange);
    this.stopSeekPump();
    this.releaseObjectUrls();
    this.setDrawingScrollLock(false);
  }

  private updateCompareAvailability() {
    this.canUseCompareView = !window.matchMedia(this.compareViewMediaQuery).matches;
    if (!this.canUseCompareView && this.reviewTab === 'compare') {
      this.setReviewTab('this');
    }
  }

  async setupSession() {
    let allSwings = await this.sessionService.searchSwings(this.sessionId);
    this.swings = [];
    allSwings
    .sort((a, b) => b.sequence - a.sequence) // Sort descending by sequence
    .forEach(s => {
      this.swings.push({ swing: s, isSelected: false });
    });

    if(this.swings && this.swings.length > 0)
      this.selectSwing(this.swings[0]);

    this.canCaptureSwing = await this.authenticationService.userHasPermission(PERMISSIONS.RECORDSWING);
  }

  selectSwing(s:SwingViewModel):void {
    if(this.selectedSwing)
      this.selectedSwing.isSelected = false;
    this.selectedSwing = s;
    const faceOnPath = (this.selectedSwing.swing.faceOnUrl ?? '').trim();
    const dtlPath = (this.selectedSwing.swing.dtlUrl ?? '').trim();
    this.hasFaceOnClip = faceOnPath.length > 0;
    this.hasDtlClip = dtlPath.length > 0;
    if (this.hasDtlClip && !this.hasFaceOnClip) {
      this.activeReviewCamera = 'dtl';
    } else if (this.hasFaceOnClip) {
      this.activeReviewCamera = 'faceon';
    }
    let baseVideoUrl = this.configService.getMediaBaseUrl();
    this.loadSwingVideos(
      this.hasFaceOnClip ? baseVideoUrl + faceOnPath : '',
      this.hasDtlClip ? baseVideoUrl + dtlPath : ''
    );
    this.selectedSwing.isSelected = true;
    this.notes = this.selectedSwing.swing.notes;
    this.tags = this.selectedSwing.swing.tags;
    this.selectedResult = this.selectedSwing.swing.result ?? null;

    this.shotData = this.selectedSwing.swing.shotData ?? this.sessionService.getSuppportedShotData();
    this.currentTime = 0;
    this.duration = 0;
    this.isPlaying = false;
    this.isScrubbing = false;
    this.desiredTime = 0;
    this.lastCommitAt = 0;
    this.videosFullyLocal = false;
    this.stopSeekPump();
  }

  setReviewTab(tab: 'this' | 'compare') {
    if (tab === 'compare') {
      this.pauseVideo();
      this.mode = null;
      this.setDrawingScrollLock(false);
    }
    this.reviewTab = tab;
    if (tab === 'this') {
      this.compareSwings?.pauseBoth();
      this.compareSwings?.exitDrawMode();
      this.wakeActiveReviewVideo();
    }
  }

  get drawMode(): 'circle' | 'line' | null {
    return this.reviewTab === 'compare' ? (this.compareSwings?.mode ?? null) : this.mode;
  }

  get compareCamera(): 'faceon' | 'dtl' {
    return this.compareSwings?.activeCamera ?? 'faceon';
  }

  setCompareCamera(camera: 'faceon' | 'dtl') {
    this.compareSwings?.setCamera(camera);
  }

  swingResultLabel(result: SwingResult | null | undefined): string {
    return swingResultLabel(result);
  }

  setReviewCamera(camera: 'faceon' | 'dtl') {
    this.activeReviewCamera = camera;
    this.wakeActiveReviewVideo();
    this.seekBoth(this.currentTime);
    if (this.isPlaying) {
      this.getVideos().forEach(video => {
        video.playbackRate = Number(this.playbackSpeed) || 1;
        video.play()?.catch(() => {});
      });
    }
  }

  toggleDrawMode(nextMode: 'line' | 'circle') {
    if (this.reviewTab === 'compare') {
      this.compareSwings?.toggleDrawMode(nextMode);
      return;
    }
    this.mode = this.mode === nextMode ? null : nextMode;
    this.setDrawingScrollLock(this.mode !== null);
  }

  private setDrawingScrollLock(locked: boolean) {
    document.documentElement.classList.toggle('session-drawing-lock', locked);
    document.body.classList.toggle('session-drawing-lock', locked);
    document.removeEventListener('touchmove', this.preventTouchScroll);
    if (locked) {
      document.addEventListener('touchmove', this.preventTouchScroll, { passive: false });
    }
  }

  private preventTouchScroll = (event: TouchEvent) => {
    event.preventDefault();
  };

  getShotDataEntries() {
    if(!this.shotData)
      return [];

    return Object.entries(this.shotData);
  }

  async saveShotData():Promise<void> {
    if(!this.selectedSwing || !this.shotData) return;
    let id = this.selectedSwing.swing.id;
    let success = await this.sessionService.saveShotData(id, this.shotData);
    if(success)
      this.selectedSwing.swing.shotData = this.shotData;
  }

  // Menu methods
  setActiveMenu(menu: string) {
    this.activeMenu = menu;
    console.log('Active menu:', menu);
  }

  // Accordion methods
  toggleAccordion(accordion: string) {
    if (this.openAccordions.has(accordion)) {
      this.openAccordions.delete(accordion);
    } else {
      this.openAccordions.add(accordion);
    }
  }

  isAccordionOpen(accordion: string): boolean {
    return this.openAccordions.has(accordion);
  }

  async onNotesAutosave(updatedNotes: string) {
    if(!this.selectedSwing) return;
    let success = await this.sessionService.saveSwingNote(this.selectedSwing.swing.id, updatedNotes);
    if(success)
      this.selectedSwing.swing.notes = updatedNotes;
  }

  async downloadSwingPackage() {
    if (!this.selectedSwing || this.isDownloadingPackage) {
      return;
    }
    if (!this.hasFaceOnClip && !this.hasDtlClip) {
      return;
    }

    this.isDownloadingPackage = true;
    try {
      const swing = this.selectedSwing.swing;
      const files: Record<string, Uint8Array> = {
        'swing.md': markdownToBytes(buildSwingMarkdown(this.session, swing))
      };

      if (this.hasFaceOnClip) {
        files[videoFileName(swing.faceOnUrl, 'face-on')] =
          await blobToBytes(await this.fetchSwingVideo(this.faceOnVideoUrl));
      }
      if (this.hasDtlClip) {
        files[videoFileName(swing.dtlUrl, 'down-the-line')] =
          await blobToBytes(await this.fetchSwingVideo(this.dtlVideoUrl));
      }

      const zipped = await zipSwingPackage(files);
      const zipBlob = new Blob([zipped as BlobPart], { type: 'application/zip' });
      const href = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = href;
      link.download = swingExportZipName(this.session, swing);
      link.click();
      URL.revokeObjectURL(href);
    } catch {
      window.alert('Could not download this swing. Try again after the videos finish loading.');
    } finally {
      this.isDownloadingPackage = false;
    }
  }

  private async fetchSwingVideo(url: string): Promise<Blob> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Video download failed');
    }
    return response.blob();
  }

  async setSwingResult(result: SwingResult) {
    if (!this.selectedSwing) return;
    const next = this.selectedResult === result ? null : result;
    this.selectedResult = next;
    this.selectedSwing.swing.result = next;
    await this.sessionService.saveSwingResult(this.selectedSwing.swing.id, next);
  }

  // Tags methods
  async addTag(event: KeyboardEvent) {
    if(!this.selectedSwing) return;

    if (event.key === 'Enter' && this.newTag.trim()) {
      const tag = this.newTag.trim();
      if (!this.tags.includes(tag)) {
        let success = await this.sessionService.saveSwingTag(this.selectedSwing.swing.id, tag);
        if(success) {
          this.selectedSwing.swing.tags.push(tag);
          this.tags = this.selectedSwing.swing.tags;
        }
      }
      this.newTag = '';
    }
  }

  async removeTag(tag: string) {
    if(!this.selectedSwing) return;
    let success = await this.sessionService.removeSwingTag(this.selectedSwing.swing.id, tag)
    if(success) {
      this.selectedSwing.swing.tags = this.selectedSwing.swing.tags = this.tags.filter(t => t !== tag);
      this.tags = this.selectedSwing.swing.tags;
    }
  }

  playVideo() {
    this.isScrubbing = false;
    this.getVideos().forEach(video => {
      video.playbackRate = Number(this.playbackSpeed) || 1;
      const playAttempt = video.play();
      playAttempt?.catch(() => {});
    });
    this.isPlaying = true;
  }

  pauseVideo() {
    this.getVideos().forEach(video => video.pause());
    this.isPlaying = false;
    if (!this.isScrubbing) {
      this.currentTime = this.getMasterTime();
    }
  }

  onScrubStart() {
    this.isScrubbing = true;
    if (this.isPlaying) {
      this.getVideos().forEach(video => video.pause());
      this.isPlaying = false;
    }
    window.removeEventListener('pointerup', this.onPointerUpScrubEnd);
    window.addEventListener('pointerup', this.onPointerUpScrubEnd);
  }

  onScrub(value: number | string) {
    this.isScrubbing = true;
    this.seekBoth(Number(value));
  }

  onScrubEnd() {
    this.isScrubbing = false;
    window.removeEventListener('pointerup', this.onPointerUpScrubEnd);
  }

  onVideoCanPlay(camera: 'faceon' | 'dtl') {
    if (camera === 'faceon') {
      this.isFaceOnLoading = false;
    } else {
      this.isDtlLoading = false;
    }
    this.onVideoMetadata();
  }

  onVideoLoadError(camera: 'faceon' | 'dtl') {
    if (camera === 'faceon') {
      this.isFaceOnLoading = false;
    } else {
      this.isDtlLoading = false;
    }
  }

  onVideoMetadata() {
    this.duration = this.getSharedDuration();
    this.applySavedStartIfNeeded();
    if (!this.startSeekPending && this.duration && this.currentTime > this.duration) {
      this.seekBoth(this.duration);
    }
  }

  onTimeUpdate(event: Event) {
    if (this.isScrubbing || this.isDrawing || this.isDragging) {
      return;
    }

    const video = event.target as HTMLVideoElement;
    if (!isFinite(video.currentTime)) {
      return;
    }

    this.currentTime = video.currentTime;
    this.desiredTime = this.currentTime;
  }

  onPlaybackEnded() {
    this.pauseVideo();
  }

  get scrubPercent(): string {
    if (!this.duration) {
      return '0%';
    }
    return `${Math.min(100, Math.max(0, (this.currentTime / this.duration) * 100))}%`;
  }

  formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) {
      return '0.00s';
    }
    return `${seconds.toFixed(2)}s`;
  }

  savedReviewStartTime(): number {
    const swing = this.selectedSwing?.swing;
    if (!swing) {
      return 0;
    }
    const value = swing.faceOnStartTime ?? swing.dtlStartTime;
    return value == null || !isFinite(value) ? 0 : value;
  }

  hasSavedReviewStart(): boolean {
    const swing = this.selectedSwing?.swing;
    if (!swing) {
      return false;
    }
    const value = swing.faceOnStartTime ?? swing.dtlStartTime;
    return value != null && isFinite(value);
  }

  async markStart() {
    if (!this.selectedSwing || this.savingStart) {
      return;
    }
    if (!this.hasFaceOnClip && !this.hasDtlClip) {
      return;
    }

    this.pauseVideo();
    const startTime = +this.currentTime.toFixed(4);
    this.savingStart = true;
    const swingId = this.selectedSwing.swing.id;
    const [faceOnSaved, dtlSaved] = await Promise.all([
      this.sessionService.saveSwingStartTime(swingId, 'faceon', startTime),
      this.sessionService.saveSwingStartTime(swingId, 'dtl', startTime)
    ]);
    this.savingStart = false;
    if (!faceOnSaved || !dtlSaved) {
      return;
    }

    this.selectedSwing.swing.faceOnStartTime = startTime;
    this.selectedSwing.swing.dtlStartTime = startTime;
  }

  private applySavedStartIfNeeded() {
    if (!this.startSeekPending) {
      return;
    }
    if (this.getReadyVideos().length === 0) {
      return;
    }

    this.seekBoth(this.savedReviewStartTime());
    const expected = (this.hasFaceOnClip ? 1 : 0) + (this.hasDtlClip ? 1 : 0);
    if (expected > 0 && this.getReadyVideos().length >= expected) {
      this.startSeekPending = false;
    }
  }

  // MP4 (default) seeks over HTTP. Legacy MediaRecorder WebM needs a full
  // download + remux before scrubbing works.
  private loadSwingVideos(faceOnUrl: string, dtlUrl: string) {
    const token = ++this.videoLoadToken;
    this.releaseObjectUrls();

    this.faceOnVideoUrl = '';
    this.dtlVideoUrl = '';
    this.videosFullyLocal = false;
    this.duration = 0;
    this.isFaceOnLoading = this.hasFaceOnClip;
    this.isDtlLoading = this.hasDtlClip;
    this.startSeekPending = true;

    const needsRemux = this.isLegacyWebmUrl(faceOnUrl) || this.isLegacyWebmUrl(dtlUrl);
    if (!needsRemux) {
      this.faceOnVideoUrl = faceOnUrl;
      this.dtlVideoUrl = dtlUrl;
      this.videosFullyLocal = true;
      this.isBufferingVideos = false;
      this.currentTime = 0;
      this.desiredTime = 0;
      return;
    }

    this.isBufferingVideos = true;

    Promise.all([this.downloadToObjectUrl(faceOnUrl), this.downloadToObjectUrl(dtlUrl)])
      .then(([faceOnLocal, dtlLocal]) => {
        if (token !== this.videoLoadToken) {
          [faceOnLocal, dtlLocal].forEach(url => url && URL.revokeObjectURL(url));
          return;
        }

        const loadedLocally = !!(faceOnLocal || !faceOnUrl) && !!(dtlLocal || !dtlUrl);

        if (faceOnLocal) {
          this.objectUrls.push(faceOnLocal);
          this.faceOnVideoUrl = faceOnLocal;
        } else {
          this.faceOnVideoUrl = faceOnUrl;
        }

        if (dtlLocal) {
          this.objectUrls.push(dtlLocal);
          this.dtlVideoUrl = dtlLocal;
        } else {
          this.dtlVideoUrl = dtlUrl;
        }

        this.videosFullyLocal = loadedLocally;
        this.currentTime = 0;
        this.desiredTime = 0;
      })
      .finally(() => {
        if (token === this.videoLoadToken) {
          this.isBufferingVideos = false;
        }
      });
  }

  private isLegacyWebmUrl(url: string): boolean {
    return /\.webm(\?|#|$)/i.test(url);
  }

  private async downloadToObjectUrl(url: string): Promise<string | null> {
    if (!url) {
      return null;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const raw = await response.blob();
      const seekable = await makeWebmSeekable(raw);
      return URL.createObjectURL(seekable);
    } catch {
      // Cross-origin or offline: keep streaming straight from the URL.
      return null;
    }
  }

  private releaseObjectUrls() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }

  private seekBoth(time: number) {
    const max = this.getSharedDuration();
    const safeTime = Math.max(0, Math.min(max || 0, time));
    this.currentTime = +safeTime.toFixed(4);
    this.desiredTime = this.currentTime;
    this.startSeekPump();
  }

  private startSeekPump() {
    if (this.seekPump) {
      return;
    }
    this.pumpSeek();
    this.seekPump = setInterval(() => this.pumpSeek(), 80);
  }

  private stopSeekPump() {
    if (this.seekPump) {
      clearInterval(this.seekPump);
      this.seekPump = null;
    }
  }

  private pumpSeek() {
    const videos = this.getReadyVideos();
    if (videos.length === 0) {
      return;
    }

    const now = performance.now();

    if (videos.some(video => video.seeking)) {
      // A seek into bytes the browser does not have can never finish. Drop it
      // instead of letting it block every seek behind it.
      if (now - this.seekIssuedAt > this.seekTimeoutMs) {
        this.desiredTime = this.getMasterTime();
        this.currentTime = this.desiredTime;
        this.stopSeekPump();
      }
      return;
    }

    const targets = videos
      .map(video => ({ video, time: this.reachableTime(video, this.desiredTime) }))
      .filter(({ video, time }) => Math.abs(video.currentTime - time) >= this.frameStep * 0.5);

    if (targets.length === 0) {
      if (!this.isScrubbing) {
        this.stopSeekPump();
      }
      return;
    }

    if (this.isScrubbing && now - this.lastCommitAt < this.minCommitIntervalMs) {
      return;
    }

    this.lastCommitAt = now;
    this.seekIssuedAt = now;
    targets.forEach(({ video, time }) => {
      video.currentTime = time;
    });
  }

  private reachableTime(video: HTMLVideoElement, time: number): number {
    const wanted = Math.max(0, Math.min(this.videoDuration(video), time));

    // After remux + blob load the whole file is local; trust the requested time
    // instead of Chrome's incomplete seekable ranges for MediaRecorder WebM.
    if (this.videosFullyLocal) {
      return wanted;
    }

    const ranges = video.seekable;
    if (!ranges || ranges.length === 0) {
      return wanted;
    }

    for (let i = 0; i < ranges.length; i++) {
      if (wanted >= ranges.start(i) && wanted <= ranges.end(i)) {
        return wanted;
      }
    }

    return Math.max(0, Math.min(wanted, ranges.end(ranges.length - 1)));
  }

  private videoDuration(video: HTMLVideoElement): number {
    if (isFinite(video.duration) && video.duration > 0) {
      return video.duration;
    }

    // MediaRecorder WebM often reports an unknown duration until it is fully read.
    const ranges = video.seekable;
    return ranges && ranges.length > 0 ? ranges.end(ranges.length - 1) : 0;
  }

  private getReadyVideos(): HTMLVideoElement[] {
    return this.getVideos().filter(video => this.videoDuration(video) > 0);
  }

  private getSharedDuration(): number {
    const durations = this.getVideos()
      .map(video => this.videoDuration(video))
      .filter(duration => duration > 0);

    if (durations.length === 0) {
      return 0;
    }

    return Math.min(...durations);
  }

  private getMasterTime(): number {
    const master = this.activeReviewCamera === 'dtl'
      ? this.downTheLineVideoElement?.nativeElement
      : this.faceOnVideoElement?.nativeElement;
    if (master && isFinite(master.currentTime)) {
      return master.currentTime;
    }
    return this.currentTime;
  }

  private getVideos(): HTMLVideoElement[] {
    const videos: HTMLVideoElement[] = [];
    if (this.faceOnVideoElement?.nativeElement) {
      videos.push(this.faceOnVideoElement.nativeElement);
    }
    if (this.downTheLineVideoElement?.nativeElement) {
      videos.push(this.downTheLineVideoElement.nativeElement);
    }
    return videos;
  }

  private getActiveReviewVideo(): HTMLVideoElement | null {
    const video = this.activeReviewCamera === 'dtl'
      ? this.downTheLineVideoElement?.nativeElement
      : this.faceOnVideoElement?.nativeElement;
    return video ?? null;
  }

  private wakeActiveReviewVideo() {
    const video = this.getActiveReviewVideo();
    if (!video || !video.src) {
      return;
    }

    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      video.load();
    }

    const paintFrame = () => {
      if (isFinite(this.currentTime)) {
        video.currentTime = this.currentTime;
      }
      video.playbackRate = Number(this.playbackSpeed) || 1;
      if (this.isPlaying) {
        video.play()?.catch(() => {});
        return;
      }
      video.play()?.then(() => {
        if (!this.isPlaying) {
          video.pause();
        }
      }).catch(() => {});
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      paintFrame();
      return;
    }

    video.addEventListener('loadeddata', paintFrame, { once: true });
  }

  resetVideo() {    
    this.pauseVideo();
    this.seekBoth(this.savedReviewStartTime());
    this.isPlaying = false;
  }
  
  backToSession() {
    this.showSwingCapture = false;
    this.setupSession();
  }


/*** SHAPE DRAWING */


faceOnShapes:Shape[] = [];
downTheLineShapes:Shape[] = [];
isDrawing = false;
isDragging = false;
currentShape:Shape|null = null;
mode: 'circle' | 'line' | null = null;
selectedShape:Shape|null = null;
private dragStartPoint:{ x: number; y: number } | null = null;
private dragOrigin: { cx?: number; cy?: number; x1?: number; y1?: number; x2?: number; y2?: number } | null = null;
private shapeMoved = false;
private readonly dragThreshold = 4;

@HostListener('document:keydown', ['$event'])
handleDeleteKey(event: KeyboardEvent) {
  if (this.reviewTab === 'compare') {
    return;
  }
  if (event.key === 'Delete' && this.selectedShape) {
    this.deleteSelectedShape();
    event.preventDefault();
  }
}

onMouseDown(event: MouseEvent, videoSource:string) {
  if (!this.mode) {
    return;
  }
  event.preventDefault();
  this.capturePointer(event);
  let arrayToManage = (videoSource === 'faceon') ? this.faceOnShapes : this.downTheLineShapes;
  if(!event || !event.target) return;
  const pt = this.getSVGPoint(this.getOverlaySvg(event), event);
  if(!pt) return;

  this.clearSelection(videoSource);
  this.shapeMoved = false;

  if (this.mode === 'circle') {
    this.currentShape = {
      type: 'circle',
      cx: pt.x,
      cy: pt.y,
      r: 0,
      selected: false
    };
  } else if (this.mode === 'line') {
    this.currentShape = {
      type: 'line',
      x1: pt.x,
      y1: pt.y,
      x2: pt.x,
      y2: pt.y,
      selected: false
    };
  }

  this.isDrawing = true;
  this.isDragging = false;
  if(this.currentShape)
    arrayToManage.push(this.currentShape);
}

onShapeMouseDown(event: MouseEvent, shape:Shape, videoSource:string) {
  event.stopPropagation();
  event.preventDefault();
  this.capturePointer(event);

  const svg = (event.target as SVGElement).closest('svg');
  if(!svg) return;

  const pt = this.getSVGPoint(svg, event);
  if(!pt) return;

  this.selectShape(event, shape, videoSource);
  this.isDragging = true;
  this.isDrawing = false;
  this.shapeMoved = false;
  this.currentShape = shape;
  this.dragStartPoint = { x: pt.x, y: pt.y };

  if (shape.type === 'circle') {
    this.dragOrigin = { cx: shape.cx, cy: shape.cy };
  } else {
    this.dragOrigin = { x1: shape.x1, y1: shape.y1, x2: shape.x2, y2: shape.y2 };
  }
}

onMouseMove(event: MouseEvent, videoSource:string) {
  if ((!this.isDrawing && !this.isDragging) || !this.currentShape) return;
  event.preventDefault();

  const svg = this.getOverlaySvg(event);
  if(!svg) return;

  const pt = this.getSVGPoint(svg, event);
  if(!pt) return;

  if (this.isDragging && this.dragStartPoint && this.dragOrigin) {
    const dx = pt.x - this.dragStartPoint.x;
    const dy = pt.y - this.dragStartPoint.y;

    if (Math.hypot(dx, dy) > this.dragThreshold) {
      this.shapeMoved = true;
    }

    if (this.currentShape.type === 'circle') {
      this.currentShape.cx = (this.dragOrigin.cx ?? 0) + dx;
      this.currentShape.cy = (this.dragOrigin.cy ?? 0) + dy;
    } else {
      this.currentShape.x1 = (this.dragOrigin.x1 ?? 0) + dx;
      this.currentShape.y1 = (this.dragOrigin.y1 ?? 0) + dy;
      this.currentShape.x2 = (this.dragOrigin.x2 ?? 0) + dx;
      this.currentShape.y2 = (this.dragOrigin.y2 ?? 0) + dy;
    }
    return;
  }
  
  if (this.currentShape.type === 'circle') {
    const dx = pt.x - this.currentShape.cx;
    const dy = pt.y - this.currentShape.cy;
    this.currentShape.r = Math.sqrt(dx*dx + dy*dy);
  } else if (this.currentShape.type === 'line') {
    this.currentShape.x2 = pt.x;
    this.currentShape.y2 = pt.y;
  }
}

onMouseUp(event: MouseEvent, videoSource:string) {
  this.releasePointer(event);
  if (this.isDragging && this.shapeMoved) {
    this.clearSelection(videoSource);
  }

  this.isDrawing = false;
  this.isDragging = false;
  this.currentShape = null;
  this.dragStartPoint = null;
  this.dragOrigin = null;
}

selectShape(event: MouseEvent, shape:Shape, videoSource:string) {
  event.stopPropagation();
  if (this.shapeMoved) {
    this.shapeMoved = false;
    return;
  }

  let arrayToManage = (videoSource === 'faceon') ? this.faceOnShapes : this.downTheLineShapes;
  arrayToManage.forEach(s => s.selected = false);
  shape.selected = true;
  this.selectedShape = shape;
}

clearSelection(videoSource:string) {
  let arrayToManage = (videoSource === 'faceon') ? this.faceOnShapes : this.downTheLineShapes;
  arrayToManage.forEach(s => s.selected = false);
  this.selectedShape = null;
}

deleteSelectedShape() {
  if (!this.selectedShape) return;
  this.faceOnShapes = this.faceOnShapes.filter(s => s !== this.selectedShape);
  this.downTheLineShapes = this.downTheLineShapes.filter(s => s !== this.selectedShape);
  this.selectedShape = null;
}

clearAllShapes(event: Event, videoSource: string) {
  event.preventDefault();
  event.stopPropagation();

  if (videoSource === 'faceon') {
    if (this.selectedShape && this.faceOnShapes.includes(this.selectedShape)) {
      this.selectedShape = null;
    }
    this.faceOnShapes = [];
    return;
  }

  if (this.selectedShape && this.downTheLineShapes.includes(this.selectedShape)) {
    this.selectedShape = null;
  }
  this.downTheLineShapes = [];
}

// Utility: Convert screen coords to SVG coords
getSVGPoint(svg: SVGSVGElement | null, event: MouseEvent) {
  if(!svg) return;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  return pt.matrixTransform(svg.getScreenCTM()?.inverse());
}

private getOverlaySvg(event: Event): SVGSVGElement | null {
  const from = (event.currentTarget as Element | null) ?? (event.target as Element | null);
  if (!from) {
    return null;
  }
  if (from instanceof SVGElement) {
    return from.closest('svg');
  }
  return from.closest('.video-content')?.querySelector('svg.overlay') as SVGSVGElement | null;
}

private capturePointer(event: MouseEvent) {
  const pointerEvent = event as PointerEvent;
  if (typeof pointerEvent.pointerId !== 'number') {
    return;
  }
  const target = (event.currentTarget as Element | null) ?? (event.target as Element | null)?.closest?.('svg');
  if (target && 'setPointerCapture' in target) {
    try {
      (target as Element).setPointerCapture(pointerEvent.pointerId);
    } catch {
      // Capture is best-effort; drawing still works without it.
    }
  }
}

private releasePointer(event: MouseEvent) {
  const pointerEvent = event as PointerEvent;
  if (typeof pointerEvent.pointerId !== 'number') {
    return;
  }
  const target = (event.currentTarget as Element | null) ?? (event.target as Element | null)?.closest?.('svg');
  if (target && 'releasePointerCapture' in target) {
    try {
      (target as Element).releasePointerCapture(pointerEvent.pointerId);
    } catch {
      // Already released.
    }
  }
}



/*** END SHAPE DRAWING */

}

interface SwingViewModel {
  swing:Swing;
  isSelected:boolean
}